import axios from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { postForceUnbindCard, useAdminCardsCache } from '../../../api/admin-cards';
import { publishToast } from '../../../components/feedback';

/**
 * Encapsulates the force-unbind orchestration for the cards surface
 * (AUT-126): fires the destructive `recurring_unbind` POST, a success /
 * error toast, and invalidates the `['admin','cards']` cache so the list
 * (standalone page + inspector-detail Cards tab) refetches. Extracted from
 * the page/dialog so the bodies stay under the line cap. The action is role
 * + permission (`process_payouts`) + audit gated server-side (AUT-138
 * removed per-action TOTP step-up).
 *
 * The action is offered ONLY for `active` cards (the caller status-gates
 * the button — the backend seam is a guaranteed no-op for any other
 * status).
 *
 * 409 ERROR MAPPING (architect guardrail #3). The shared response
 * interceptor auto-toasts a generic "another admin already acted — refresh"
 * for ANY 409 (toast id `http-409`). That copy is WRONG for the force-unbind
 * `card_has_pending_payouts` 409 (the card has scheduled payouts that would
 * be orphaned — a financial-safety block, not a concurrency conflict). We
 * read `err.response?.data?.code === 'card_has_pending_payouts'` and publish
 * a CARD-SPECIFIC toast with the STABLE id `card-409` — same-id toasts dedupe
 * in the bus, so the generic interceptor toast is superseded rather than
 * stacked. On ANY rejection we `invalidate` so the status-gated action
 * eligibility re-syncs from fresh data.
 */

/** The 409 code the backend emits when the card has scheduled payouts. */
const PENDING_PAYOUTS_CODE = 'card_has_pending_payouts';

/** Stable id that dedups the card-specific 409 toast vs the interceptor's. */
const PENDING_PAYOUTS_TOAST_ID = 'card-409';

/** True when the rejection is the `card_has_pending_payouts` 409. */
const isPendingPayoutsError = (err: unknown): boolean =>
  axios.isAxiosError(err) &&
  err.response?.status === 409 &&
  (err.response.data as { code?: string } | undefined)?.code === PENDING_PAYOUTS_CODE;

export interface UseForceUnbindResult {
  /** True while the mutation is in flight. */
  busy: boolean;
  /** Run a force-unbind; resolves true on success, false on error. */
  run: (id: string) => Promise<boolean>;
}

export const useForceUnbind = (): UseForceUnbindResult => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const { invalidate } = useAdminCardsCache();

  const run = async (id: string): Promise<boolean> => {
    setBusy(true);
    try {
      await postForceUnbindCard(id);
      await invalidate();
      publishToast({
        severity: 'success',
        durationMs: 4000,
        message: t('cards.forceUnbind.successToast'),
      });
      return true;
    } catch (err: unknown) {
      // Re-sync the status-gated action eligibility from fresh data.
      await invalidate();
      if (isPendingPayoutsError(err)) {
        // Supersede the interceptor's generic 409 toast via a stable id.
        publishToast({
          id: PENDING_PAYOUTS_TOAST_ID,
          severity: 'error',
          durationMs: 8000,
          message: t('cards.forceUnbind.error.pendingPayouts'),
        });
        return false;
      }
      publishToast({
        severity: 'error',
        durationMs: 6000,
        message: t('cards.forceUnbind.error.generic'),
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, run };
};
